// CtaBanner.js
// -----------------------------------------------------------------------------
// Full-width call-to-action band. Brand- or accent-colored, with a title,
// optional supporting text, and an action slot (one or more buttons).
//
// Props:
//   - title    : node     (required)        banner heading
//   - text     : node     (default undef)   supporting text
//   - eyebrow  : string   (default undef)   small uppercase kicker
//   - action   : node     (default undef)   action slot (buttons)
//   - color    : 'brand'|'accent'|'dark'  (default 'brand') background variant
//   - align    : 'left'|'center'          (default 'center')
//   - sx       : object   (default {})      extra Joy sx overrides
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Container from './Container';

// Dark ink for the accent (orange) surface. Per the shared palette spec, accent
// 500 and lighter need dark text for AA contrast; no Joy token maps to it.
const ACCENT_CONTRAST_TEXT = '#1A1A1A';

const COLOR_VARIANTS = {
  brand: { bgcolor: 'primary.500', text: 'common.white', eyebrow: 'accent.300' },
  accent: { bgcolor: 'accent.500', text: ACCENT_CONTRAST_TEXT, eyebrow: 'primary.700' },
  dark: { bgcolor: 'primary.800', text: 'common.white', eyebrow: 'accent.300' },
};

const CtaBanner = ({
  title,
  text,
  eyebrow,
  action,
  color = 'brand',
  align = 'center',
  sx = {},
}) => {
  // Zero-trust: guard required prop and constrain enums.
  if (title === undefined || title === null || title === '') {
    return null;
  }
  const variant = COLOR_VARIANTS[color] || COLOR_VARIANTS.brand;
  const resolvedAlign = align === 'left' ? 'left' : 'center';

  return (
    <Box
      component="section"
      sx={{ width: '100%', bgcolor: variant.bgcolor, py: { xs: 7, md: 9 }, ...sx }}
    >
      <Container>
        <Stack
          direction={{ xs: 'column', md: resolvedAlign === 'center' ? 'column' : 'row' }}
          spacing={3}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            textAlign: resolvedAlign,
          }}
        >
          <Box
            sx={{
              maxWidth: resolvedAlign === 'center' ? '720px' : '640px',
              mx: resolvedAlign === 'center' ? 'auto' : 0,
            }}
          >
            {eyebrow ? (
              <Typography level="title" sx={{ color: variant.eyebrow, mb: 1 }}>
                {eyebrow}
              </Typography>
            ) : null}
            <Typography level="h2" sx={{ color: variant.text }}>
              {title}
            </Typography>
            {text ? (
              <Typography
                level="body-lg"
                sx={{ color: variant.text, opacity: 0.92, mt: 1.5 }}
              >
                {text}
              </Typography>
            ) : null}
          </Box>

          {action ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                justifyContent: 'center',
              }}
            >
              {action}
            </Stack>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
};

CtaBanner.propTypes = {
  title: PropTypes.node.isRequired,
  text: PropTypes.node,
  eyebrow: PropTypes.string,
  action: PropTypes.node,
  color: PropTypes.oneOf(['brand', 'accent', 'dark']),
  align: PropTypes.oneOf(['left', 'center']),
  sx: PropTypes.object,
};

export default CtaBanner;
